import React from 'react';
import { AbsoluteFill, useCurrentFrame, useRemotionEnvironment, useVideoConfig } from 'remotion';
import {
  WATERMARK_DESIGN_HEIGHT,
  WATERMARK_DESIGN_WIDTH,
  WATERMARK_HEIGHT_FRACTION,
  clampWatermarkPosition,
  type WatermarkPosition,
} from '@/lib/watermark/watermarkPosition';
import {
  WATERMARK_DELAY_SECONDS,
  WATERMARK_LOOP_SECONDS,
  resolveWatermarkStartSeconds,
} from '@/lib/watermark/resolveWatermarkStart';
import { watermarkTimeline } from '@/lib/watermark/watermarkTimeline';
import { useWatermarkFont } from './useWatermarkFont';
import { WatermarkArt } from './WatermarkArt';

export interface AutoclipperWatermarkProps {
  position?: WatermarkPosition | null;
  delaySeconds?: number;
}

/**
 * The free-plan Autoclipper watermark: an 8s looping animation that appears
 * `resolveWatermarkStartSeconds` into the composition, centered on the
 * (safe-zone clamped) user position at 9% of the frame height. The same
 * component draws the editor preview and the export, so they match.
 * Client-side still renders (cover capture) get nothing: covers stay clean.
 */
export const AutoclipperWatermark: React.FC<AutoclipperWatermarkProps> = ({
  position,
  delaySeconds = WATERMARK_DELAY_SECONDS,
}) => {
  useWatermarkFont();
  const env = useRemotionEnvironment();
  const frame = useCurrentFrame();
  const { fps, width, height, durationInFrames } = useVideoConfig();
  if (env.isClientSideRendering) return null;

  // A malformed overlay payload must not produce a NaN start.
  const delay = Number.isFinite(delaySeconds) ? delaySeconds : WATERMARK_DELAY_SECONDS;
  const startSeconds = resolveWatermarkStartSeconds(durationInFrames / fps, delay);
  const local = frame / fps - startSeconds;
  if (local < 0) return null;

  const state = watermarkTimeline(local % WATERMARK_LOOP_SECONDS);
  const center = clampWatermarkPosition(position, width / height);
  const scale = (height * WATERMARK_HEIGHT_FRACTION) / WATERMARK_DESIGN_HEIGHT;

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <div
        data-testid="autoclipper-watermark"
        style={{
          position: 'absolute',
          left: `${center.x * width}px`,
          top: `${center.y * height}px`,
          width: WATERMARK_DESIGN_WIDTH,
          height: WATERMARK_DESIGN_HEIGHT,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <WatermarkArt state={state} />
      </div>
    </AbsoluteFill>
  );
};
